<template>
  <!-- The "write a resume for this job with AI" dock, at the bottom of a job. One place, four states: ready (the button), not available
       (the button, disabled, with what to do about it on hover), writing (a live progress panel) and done (the new resume, and what it cost). -->
  <div class="relative flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-[#1C2128]/95 backdrop-blur shadow-[0_-8px_24px_-16px_rgba(15,23,42,0.35)]" @keydown.esc="choosing = false">
    <!-- Which resume to write from, asked only when there is more than one (the default and the templates) -->
    <template v-if="choosing">
      <div class="fixed inset-0 z-20" aria-hidden="true" @click="choosing = false"></div>
      <div class="absolute bottom-full left-0 right-0 z-30 pb-2 pointer-events-none">
        <div class="max-w-3xl mx-auto px-5 sm:px-6">
          <div role="dialog" aria-label="Choose the resume to write from" class="pointer-events-auto w-full sm:w-[28rem] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-xl p-3.5">
            <p class="text-[13px] font-semibold text-gray-900 dark:text-white">Write it from which resume?</p>
            <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">The new resume is written from the one you choose. It is never changed.</p>
            <div role="radiogroup" aria-label="Resume" class="mt-2.5 max-h-52 overflow-y-auto space-y-1.5 pr-0.5">
              <label v-for="resume in bases" :key="resume.id"
                class="flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-violet-500/40"
                :class="chosenId === resume.id ? 'border-violet-500 dark:border-violet-400 bg-violet-50 dark:bg-violet-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'">
                <input type="radio" name="base-resume" :value="resume.id" v-model="chosenId" class="w-4 h-4 flex-shrink-0 border-gray-300 dark:border-gray-600 text-violet-600 focus:ring-0 focus:ring-offset-0 focus:outline-none" />
                <span class="min-w-0 flex-1">
                  <span class="flex items-center gap-1.5">
                    <span class="truncate text-sm font-medium text-gray-900 dark:text-white">{{ resume.name }}</span>
                    <span v-if="resume.is_default" class="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">Default</span>
                    <span v-else class="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300">Template</span>
                  </span>
                  <span v-if="resume.headline" class="block truncate text-xs text-gray-500 dark:text-gray-400">{{ resume.headline }}</span>
                </span>
              </label>
            </div>
            <div class="mt-3 flex items-center justify-end gap-2">
              <button type="button" @click="choosing = false" class="h-8 px-3 rounded-lg text-[13px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">Cancel</button>
              <button type="button" @click="startChosen" class="gen-btn gen-btn-small">Write resume</button>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div class="max-w-3xl mx-auto px-5 sm:px-6 py-3">
      <!-- Writing -->
      <div v-if="active" class="gen-panel" role="status" aria-live="polite">
        <div class="flex items-center gap-3">
          <span class="gen-orb flex-shrink-0" aria-hidden="true"><svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path :d="SPARK"/></svg></span>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-semibold text-gray-900 dark:text-white">Writing your resume for this job</p>
            <p v-if="stuckInQueue" class="text-xs text-amber-600 dark:text-amber-400">Still waiting to start. If this does not change soon, the background worker may not be running.</p>
            <p v-else class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ STEPS[step].text }}</p>
          </div>
          <span class="text-xs tabular-nums text-gray-400 dark:text-gray-500">{{ elapsed }}s</span>
        </div>
        <div class="gen-track mt-2.5" aria-hidden="true"><span class="gen-bar"></span></div>
        <ol class="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
          <li v-for="(item, index) in STEPS" :key="item.label" class="flex items-center gap-1.5 text-[11px] font-medium transition-colors duration-500"
            :class="index < step ? 'text-emerald-600 dark:text-emerald-400' : index === step ? 'text-violet-700 dark:text-violet-300' : 'text-gray-400 dark:text-gray-600'">
            <svg v-if="index < step" class="w-3.5 h-3.5 gen-pop" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
            <span v-else class="w-3.5 h-3.5 inline-flex items-center justify-center" aria-hidden="true"><span class="w-1.5 h-1.5 rounded-full bg-current" :class="index === step ? 'gen-blink' : ''"></span></span>
            {{ item.label }}
          </li>
        </ol>
      </div>

      <!-- Done: the resume is in the CV Builder -->
      <div v-else-if="generation?.status === 'succeeded' && generation.resume" class="gen-done" role="status">
        <div class="flex items-center gap-3">
          <span class="w-9 h-9 rounded-xl inline-flex items-center justify-center flex-shrink-0 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 gen-pop" aria-hidden="true">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
          </span>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-semibold text-gray-900 dark:text-white">Your resume is ready</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 truncate" :title="generation.resume.name">{{ generation.resume.name }}</p>
          </div>
          <RouterLink :to="`/cv_builder/${generation.resume.id}`" data-turbo-prefetch="false" class="gen-btn gen-btn-small flex-shrink-0">
            Open resume
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/></svg>
          </RouterLink>
          <button type="button" @click="generate" :disabled="!canGenerate || starting" class="flex-shrink-0 h-8 px-3 rounded-lg text-[13px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            :title="canGenerate ? 'Write another one for this job' : undefined">Write again</button>
        </div>
        <!-- How well the resume matched the job before and after, as the AI scored them -->
        <div v-if="hasScores" class="mt-2.5 flex items-center gap-3" :aria-label="scoreLabel">
          <span class="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 flex-shrink-0">Match</span>
          <span v-if="oldScore !== null" class="text-sm font-semibold tabular-nums text-gray-400 dark:text-gray-500 flex-shrink-0">{{ oldScore }}%</span>
          <svg v-if="oldScore !== null && newScore !== null" class="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/></svg>
          <span v-if="newScore !== null" class="text-sm font-bold tabular-nums flex-shrink-0" :class="TONES[tone(newScore)].text">{{ newScore }}%</span>
          <span v-if="gain !== null" class="flex-shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded-full" :class="gain >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' : 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300'">{{ gain >= 0 ? '+' : '' }}{{ gain }}</span>
          <div class="gen-score-track relative flex-1 min-w-[4rem]" aria-hidden="true">
            <span v-if="oldScore !== null" class="gen-score-old" :style="{ width: shown ? `${oldScore}%` : '0%' }"></span>
            <span v-if="newScore !== null" class="gen-score-new" :class="TONES[tone(newScore)].bar" :style="{ width: shown ? `${newScore}%` : '0%' }"></span>
          </div>
        </div>
        <p class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
          <span class="inline-flex items-center gap-1"><svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m0 0 6.75-6.75M12 19.5l-6.75-6.75"/></svg>{{ tokens(generation.input_tokens) }} in<template v-if="generation.cached_input_tokens"> ({{ tokens(generation.cached_input_tokens) }} cached)</template></span>
          <span class="inline-flex items-center gap-1"><svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19.5v-15m0 0-6.75 6.75M12 4.5l6.75 6.75"/></svg>{{ tokens(generation.output_tokens) }} out</span>
          <span>{{ tokens(generation.total_tokens) }} tokens in all</span>
          <span v-if="cost" class="font-semibold text-gray-500 dark:text-gray-400" title="An estimate from OpenAI's prices for this model. Your OpenAI invoice has the exact amount.">about {{ cost }}</span>
          <span v-if="generation.duration_seconds">{{ Math.round(generation.duration_seconds) }}s</span>
          <span v-if="generation.base_resume" class="truncate max-w-[14rem]" :title="`Written from ${generation.base_resume.name}`">from {{ generation.base_resume.name }}</span>
          <span>{{ generation.model }}</span>
        </p>
      </div>

      <!-- Ready, or not available -->
      <div v-else class="flex items-center gap-3">
        <div class="relative group" @mouseenter="tipHeld = true" @mouseleave="tipHeld = false">
          <button type="button" @click="generate" :disabled="starting" :aria-disabled="!canGenerate" :aria-describedby="canGenerate ? undefined : 'gen-reason'"
            class="gen-btn" :class="canGenerate ? '' : 'gen-btn-off'">
            <svg v-if="starting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"/></svg>
            <svg v-else-if="canGenerate" class="w-4 h-4 gen-twirl" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path :d="SPARK"/></svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/></svg>
            {{ starting ? 'Starting' : 'Generate resume with AI' }}
          </button>

          <!-- Why it is off, and what to do. It opens above the button on hover or keyboard focus and stays open while the pointer is on it. -->
          <div v-if="!canGenerate" id="gen-reason" role="tooltip"
            class="gen-tip absolute left-0 bottom-full pb-2 w-80 z-30 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-150">
            <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-xl p-3.5">
              <p class="text-[13px] font-semibold text-gray-900 dark:text-white">{{ reason.title }}</p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{{ reason.text }}</p>
              <button v-if="reason.action === 'settings'" type="button" @click="$emit('open-ai-settings')" class="mt-2.5 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">Open AI settings</button>
              <RouterLink v-else to="/cv_builder" data-turbo-prefetch="false" class="mt-2.5 inline-block text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">Open CV Builder</RouterLink>
            </div>
          </div>
        </div>

        <p class="min-w-0 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
          <template v-if="generation?.status === 'failed'"><span class="text-red-600 dark:text-red-400 font-medium">{{ generation.error || 'The last try did not work.' }}</span></template>
          <template v-else-if="error"><span class="text-red-600 dark:text-red-400 font-medium">{{ error }}</span></template>
          <template v-else>{{ bases.length > 1 ? 'Written from the resume you choose.' : 'Written from your default resume.' }} Nothing is spent until you press it. <HelpLink path="job-tracker/generate-resume-with-ai" label="How it works" class="ml-1" /></template>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useResumeGenerationStore } from 'stores/resumeGeneration.js'
import { useJobSearchStore } from 'stores/jobSearch.js'
import { useResumesStore } from 'stores/resumes.js'
import { formatCost } from 'utils/cost.js'
import HelpLink from 'controllers/vue_components/shared/HelpLink.vue'

const props = defineProps({ job: { type: Object, required: true } })
defineEmits(['open-ai-settings'])

// The four pointed sparkle, filled.
const SPARK = 'M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5Z'

// What is going on while the AI writes. It does not know how far along it is, so these follow the time: a few seconds each, then the long
// one (the AI writing) until it is done. Finishing replaces them with the result.
const STEPS = [
  { label: 'Your resume', text: 'Reading your resume' },
  { label: 'The job', text: 'Reading the job and its company' },
  { label: 'Writing', text: 'Writing your tailored resume. This takes up to a minute or two.' },
]

const store = useResumeGenerationStore()
const settings = useJobSearchStore()
const resumes = useResumesStore()
const tipHeld = ref(false)

// The resumes it can be written from: the default and the templates. With more than one, pressing the button asks which.
const bases = computed(() => resumes.baseResumes)
const choosing = ref(false)
const chosenId = ref(null)
const preparing = ref(false)

const generation = computed(() => store.latest(props.job))
const starting = computed(() => Boolean(store.starting[props.job.id]) || preparing.value)
const error = computed(() => store.errors[props.job.id])
const active = computed(() => generation.value?.status === 'pending' || generation.value?.status === 'running')

// Whether AI can write one now: AI is on in the Job settings, and there is a default resume to write from.
const aiOn = computed(() => Boolean(settings.settings.ai_enabled))
const hasDefault = computed(() => props.job.ai_resume?.has_default_resume !== false)
const canGenerate = computed(() => aiOn.value && hasDefault.value)

const reason = computed(() => {
  if (!aiOn.value) {
    return {
      title: 'AI is turned off',
      text: 'Turn on AI in the Job settings, under AI, to write a resume for this job. It runs on your own OpenAI account, so the OpenAI integration has to be connected first.',
      action: 'settings',
    }
  }
  return {
    title: 'Make your default resume first',
    text: 'The new resume is written from your default resume. Create one in the CV Builder, and it will be used here.',
    action: 'cv',
  }
})

watch(() => props.job.id, () => {
  store.adopt(props.job)
  choosing.value = false
  resumes.fetchIfStale() // so the choice is known by the time the button is pressed
}, { immediate: true })

async function generate() {
  if (!canGenerate.value || starting.value || preparing.value) return

  preparing.value = true
  await resumes.fetchIfStale()
  preparing.value = false

  if (bases.value.length > 1) {
    chosenId.value = bases.value[0].id // the default, unless the person picks another
    choosing.value = true
    return
  }
  store.start(props.job.id)
}

function startChosen() {
  choosing.value = false
  store.start(props.job.id, chosenId.value)
}

// The time so far and the step it is at, ticking while a resume is being written.
const now = ref(Date.now())
let ticker = null
watch(active, (isActive) => {
  clearInterval(ticker)
  if (isActive) {
    now.value = Date.now()
    ticker = setInterval(() => { now.value = Date.now() }, 1000)
  }
}, { immediate: true })
onBeforeUnmount(() => clearInterval(ticker))

const elapsed = computed(() => Math.max(0, Math.round((now.value - new Date(generation.value?.created_at || now.value).getTime()) / 1000)))
const step = computed(() => (elapsed.value < 3 ? 0 : elapsed.value < 8 ? 1 : 2))
// A run that has not even been picked up after this long is waiting for a background worker that may not be there.
const QUEUE_WARNING_AFTER = 25
const stuckInQueue = computed(() => generation.value?.status === 'pending' && elapsed.value > QUEUE_WARNING_AFTER)

const tokens = (count) => (count ?? 0).toLocaleString('en-US')
const cost = computed(() => formatCost(generation.value?.cost_usd))

// The match scores the AI gave: before and after (0 to 100, or null when its answer had none). The bars grow from nothing when the result shows.
const TONES = {
  high: { text: 'text-emerald-600 dark:text-emerald-400', bar: 'gen-bar-high' },
  mid: { text: 'text-amber-600 dark:text-amber-400', bar: 'gen-bar-mid' },
  low: { text: 'text-rose-600 dark:text-rose-400', bar: 'gen-bar-low' },
}
const tone = (score) => (score >= 75 ? 'high' : score >= 50 ? 'mid' : 'low')
const number = (value) => (typeof value === 'number' ? value : null)
const oldScore = computed(() => number(generation.value?.old_match_score))
const newScore = computed(() => number(generation.value?.new_match_score))
const hasScores = computed(() => oldScore.value !== null || newScore.value !== null)
const gain = computed(() => (oldScore.value !== null && newScore.value !== null ? newScore.value - oldScore.value : null))
const scoreLabel = computed(() => (oldScore.value !== null && newScore.value !== null
  ? `Match with this job: ${oldScore.value} percent before, ${newScore.value} percent after`
  : `Match with this job: ${(newScore.value ?? oldScore.value)} percent`))

const shown = ref(false)
watch(() => generation.value?.status, (status) => {
  shown.value = false
  if (status === 'succeeded') setTimeout(() => { shown.value = true }, 80)
}, { immediate: true })
</script>

<style scoped>
@property --gen-angle { syntax: '<angle>'; inherits: false; initial-value: 0deg; }

/* The match bars: the old score as a grey bar and the new one over it in colour, both growing from nothing. */
.gen-score-track { height: 6px; border-radius: 9999px; background: rgb(148 163 184 / 0.18); overflow: hidden; }
.gen-score-old, .gen-score-new { position: absolute; left: 0; top: 0; bottom: 0; border-radius: inherit; transition: width 900ms cubic-bezier(0.2, 0.8, 0.2, 1); }
.gen-score-old { background: rgb(148 163 184 / 0.5); }
.gen-score-new { transition-delay: 150ms; }
.gen-bar-high { background: linear-gradient(90deg, #10b981, #34d399); }
.gen-bar-mid { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.gen-bar-low { background: linear-gradient(90deg, #f43f5e, #fb7185); }

/* The button: a gradient that slowly moves, a sparkle that turns, and a lift when pointed at. */
.gen-btn { display: inline-flex; align-items: center; gap: 0.5rem; height: 2.5rem; padding: 0 1.1rem; border-radius: 0.75rem; font-size: 0.8125rem; font-weight: 600; color: #fff; white-space: nowrap;
  background: linear-gradient(110deg, #4f46e5, #7c3aed, #d946ef, #4f46e5); background-size: 250% 100%; animation: gen-shimmer 6s linear infinite;
  box-shadow: 0 8px 22px -10px rgb(124 58 237 / 0.8); transition: transform 0.2s ease, box-shadow 0.2s ease; }
.gen-btn:not(:disabled):not(.gen-btn-off):hover { transform: translateY(-1px); box-shadow: 0 12px 26px -10px rgb(124 58 237 / 0.95); }
.gen-btn:not(:disabled):not(.gen-btn-off):active { transform: translateY(0) scale(0.98); }
.gen-btn:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }
.gen-btn-small { height: 2rem; padding: 0 0.8rem; font-size: 0.75rem; text-decoration: none; }
.gen-btn-off { background: #e5e7eb; color: #6b7280; animation: none; box-shadow: none; cursor: not-allowed; }
:global(.dark) .gen-btn-off { background: #2d333b; color: #768390; }
.gen-twirl { animation: gen-twirl 3s ease-in-out infinite; }
@keyframes gen-shimmer { from { background-position: 100% 0; } to { background-position: -150% 0; } }
@keyframes gen-twirl { 0%, 100% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.2); } }

/* While it writes: an orb with a ring of light turning around it, and a bar that keeps moving. */
.gen-orb { position: relative; width: 2.25rem; height: 2.25rem; border-radius: 9999px; display: inline-flex; align-items: center; justify-content: center; color: #fff;
  background: radial-gradient(circle at 30% 25%, #6366f1, #1e1b4b 75%); box-shadow: 0 0 18px rgb(99 102 241 / 0.7); animation: gen-breathe 2.6s ease-in-out infinite; }
.gen-orb::before { content: ''; position: absolute; inset: -3px; border-radius: 9999px; z-index: -1; background: conic-gradient(from var(--gen-angle), #6366f1, #22d3ee, #d946ef, #6366f1); animation: gen-turn 3s linear infinite; }
.gen-track { height: 4px; border-radius: 9999px; overflow: hidden; background: rgb(99 102 241 / 0.15); }
.gen-bar { display: block; height: 100%; width: 38%; border-radius: inherit; background: linear-gradient(90deg, #6366f1, #22d3ee, #d946ef); animation: gen-slide 1.4s ease-in-out infinite; }
.gen-blink { animation: gen-blink 1s ease-in-out infinite; }
.gen-pop { animation: gen-pop 450ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }
.gen-done, .gen-panel { animation: gen-rise 400ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
@keyframes gen-turn { to { --gen-angle: 360deg; } }
@keyframes gen-breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.07); } }
@keyframes gen-slide { 0% { transform: translateX(-110%); } 100% { transform: translateX(290%); } }
@keyframes gen-blink { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
@keyframes gen-pop { from { transform: scale(0.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes gen-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

@media (prefers-reduced-motion: reduce) {
  .gen-btn, .gen-twirl, .gen-orb, .gen-orb::before, .gen-bar, .gen-blink, .gen-pop, .gen-done, .gen-panel { animation: none; }
  .gen-score-old, .gen-score-new { transition: none; }
  .gen-bar { width: 100%; }
}
</style>
